import { cn } from '@parcel/ui';
import type * as React from 'react';

export interface DetailField {
  label: string;
  value: React.ReactNode;
  /** Masks the value in session replay and autocapture. Use for contact PII. */
  sensitive?: boolean;
}

/** Label-left, value-right property rows; two columns in the drawer, one in the rail. */
export function DetailFieldGrid({
  fields,
  columns = 2,
}: { fields: DetailField[]; columns?: 1 | 2 }) {
  return (
    <dl
      className={cn(
        'grid gap-x-8 gap-y-2.5 text-sm',
        columns === 1 ? 'grid-cols-1' : 'grid-cols-2',
      )}
    >
      {fields.map((field) => (
        <div
          key={field.label}
          className="grid min-w-0 grid-cols-[7.5rem_minmax(0,1fr)] items-baseline gap-3"
        >
          <dt className="text-muted-foreground">{field.label}</dt>
          <dd
            className={cn(
              'min-w-0 break-words text-foreground',
              field.sensitive && 'ph-no-capture',
            )}
          >
            {field.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Label-above-value cells: three across in the drawer, two in the rail. */
export function StackedFieldGrid({
  fields,
  columns = 3,
}: { fields: DetailField[]; columns?: 2 | 3 }) {
  return (
    <dl
      className={cn(
        'grid gap-x-6 gap-y-4 text-sm',
        columns === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3',
      )}
    >
      {fields.map((field) => (
        <div key={field.label} className="min-w-0">
          <dt className="text-xs text-muted-foreground">{field.label}</dt>
          <dd
            className={cn(
              'mt-1 min-w-0 break-words text-foreground',
              field.sensitive && 'ph-no-capture',
            )}
          >
            {field.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function PropertySection({
  title,
  action,
  children,
}: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="flex items-center text-sm font-semibold text-foreground">
        {title}
        {action}
      </h3>
      {children}
    </section>
  );
}

export interface RelatedListItem {
  id: string;
  avatar?: React.ReactNode;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  meta?: React.ReactNode;
  onClick?: () => void;
}

export function RelatedList({ items }: { items: RelatedListItem[] }) {
  return (
    <ul className="divide-y rounded-md border">
      {items.map((item) => (
        <RelatedListRow key={item.id} item={item} />
      ))}
    </ul>
  );
}

function RelatedListRow({ item }: { item: RelatedListItem }) {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!item.onClick) return;
    if (!shouldHandleRelatedListRowKeyDown(event)) return;

    event.preventDefault();
    item.onClick();
  };

  const content = (
    <>
      {item.avatar ? <div className="shrink-0">{item.avatar}</div> : null}
      <div className="min-w-0 flex-1 text-left">
        <div className="truncate font-medium text-foreground">{item.primary}</div>
        {item.secondary ? (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">{item.secondary}</div>
        ) : null}
      </div>
      {item.meta ? <div className="shrink-0 text-xs text-muted-foreground">{item.meta}</div> : null}
    </>
  );

  if (item.onClick) {
    return (
      <li>
        {/* biome-ignore lint/a11y/useSemanticElements: rows can contain external avatar links, so a semantic button would nest interactive elements. */}
        <div
          role="button"
          tabIndex={0}
          onClick={item.onClick}
          onKeyDown={handleKeyDown}
          className={cn(
            'flex w-full items-center justify-between gap-4 px-4 py-3 text-sm',
            'transition-colors hover:bg-muted/60 focus:outline-none focus-visible:bg-muted/60',
          )}
        >
          {content}
        </div>
      </li>
    );
  }

  return <li className="flex items-center justify-between gap-4 px-4 py-3 text-sm">{content}</li>;
}

export function shouldHandleRelatedListRowKeyDown(event: {
  key: string;
  target: EventTarget | null;
  currentTarget: EventTarget | null;
}) {
  return (event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget;
}

export function EmptyTabState({ message }: { message: string }) {
  return (
    <div className="flex h-32 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
      {message}
    </div>
  );
}
